/**
 * `mastodon` — an account's public posts, a hashtag, or the home timeline (sources.md "mastodon").
 * CORS `*` on the API, so requests go direct. The optional per-source token `source:<id>:token` is sent
 * as `Authorization: Bearer` (then `direct-only`); the home timeline requires it.
 * Cursor: `{ sinceId }` — the newest status id seen.
 */

import type { MastodonConfig, MediaItem, RawItem, SecretKey, SourceClient, SourceFetchContext, SourceFetchResult } from '../contracts/sources.js';
import { DigesterError } from '../kernel/errors.js';
import { sourceId } from '../kernel/ids.js';
import { isRecord } from './feed.js';
import { assertOk, escapeHtml, invalidInput, parseDate, parseHttpUrl, parseJson } from './http.js';

const PAGE_LIMIT = 40;

type Parsed = { instance: string; mode: MastodonConfig['mode']; acct?: string; hashtag?: string };

/** `@user@host`, `user@host`, `https://host/@user`, `https://host/tags/x`, `#tag` (+ config.instance). */
export function parseMastodonAddress(address: string, config: Partial<MastodonConfig> | undefined): Parsed | null {
  const raw = address.trim();
  const instance = config?.instance?.trim().toLowerCase();
  if (config?.mode === 'home') {
    const m = /^@?([\w.-]+)@([\w.-]+\.[a-z]{2,})$/i.exec(raw);
    if (m?.[1] && m[2]) return { instance: m[2].toLowerCase(), mode: 'home', acct: m[1] };
    if (instance && /^@?[\w.-]+$/.test(raw)) return { instance, mode: 'home', acct: raw.replace(/^@/, '') };
    return null;
  }
  const tag = /^#([\p{L}\p{N}_]+)$/u.exec(raw);
  if (tag?.[1]) return instance ? { instance, mode: 'hashtag', hashtag: tag[1] } : null;
  if (config?.mode === 'hashtag' && config.hashtag && instance) return { instance, mode: 'hashtag', hashtag: config.hashtag.replace(/^#/, '') };
  const handle = /^@?([\w.-]+)@([\w.-]+\.[a-z]{2,})$/i.exec(raw);
  if (handle?.[1] && handle[2]) {
    const host = instance ?? handle[2].toLowerCase();
    return { instance: host, mode: 'account', acct: handle[2].toLowerCase() === host ? handle[1] : `${handle[1]}@${handle[2].toLowerCase()}` };
  }
  const url = parseHttpUrl(raw);
  if (url) {
    const parts = url.pathname.split('/').filter((p) => p !== '');
    const host = url.hostname.toLowerCase();
    if (parts[0] === 'tags' && parts[1]) return { instance: host, mode: 'hashtag', hashtag: decodeURIComponent(parts[1]) };
    if (parts[0]?.startsWith('@') && parts[0].length > 1) {
      const name = parts[0].slice(1);
      return { instance: host, mode: 'account', acct: name };
    }
    return null;
  }
  if (instance && /^@?[\w.-]+$/.test(raw)) return { instance, mode: 'account', acct: raw.replace(/^@/, '') };
  return null;
}

export function mastodonLocator(p: Parsed): string {
  if (p.mode === 'hashtag') return `mastodon:${p.instance}:tag:${(p.hashtag ?? '').toLowerCase()}`;
  if (p.mode === 'home') return `mastodon:${p.instance}:home:${(p.acct ?? '').toLowerCase()}`;
  return `mastodon:${p.instance}:account:${(p.acct ?? '').toLowerCase()}`;
}

const tokenKey = (id: string): SecretKey => `source:${id}:token`;

function mediaOf(attachments: unknown): MediaItem[] {
  if (!Array.isArray(attachments)) return [];
  const out: MediaItem[] = [];
  for (const a of attachments) {
    if (!isRecord(a) || typeof a.url !== 'string') continue;
    const type = a.type === 'image' ? 'image' : a.type === 'video' || a.type === 'gifv' ? 'video' : a.type === 'audio' ? 'audio' : 'link';
    const m: MediaItem = { type, url: a.url };
    if (typeof a.preview_url === 'string') m.thumbnailUrl = a.preview_url;
    if (typeof a.description === 'string' && a.description) m.title = a.description;
    out.push(m);
  }
  return out;
}

export function statusItem(outer: Record<string, unknown>): RawItem | null {
  const reblog = isRecord(outer.reblog) ? outer.reblog : null;
  const s = reblog ?? outer;
  const uri = typeof s.uri === 'string' ? s.uri : null;
  if (!uri) return null;
  const account = isRecord(s.account) ? s.account : {};
  const item: RawItem = { externalId: uri };
  const name = typeof account.display_name === 'string' && account.display_name.trim() ? account.display_name.trim() : typeof account.acct === 'string' ? account.acct : '';
  if (name) item.author = name;
  const spoiler = typeof s.spoiler_text === 'string' && s.spoiler_text ? `<p><strong>${escapeHtml(s.spoiler_text)}</strong></p>` : '';
  const content = typeof s.content === 'string' ? s.content : '';
  if (spoiler || content) item.html = `${spoiler}${content}`;
  const media = mediaOf(s.media_attachments);
  if (media.length > 0) item.media = media;
  const image = media.find((m) => m.type === 'image');
  if (image) item.imageUrl = image.thumbnailUrl ?? image.url;
  item.url = typeof s.url === 'string' && s.url ? s.url : uri;
  const publishedAt = parseDate(typeof s.created_at === 'string' ? s.created_at : null);
  if (publishedAt !== undefined) item.publishedAt = publishedAt;
  if (typeof s.language === 'string' && s.language) item.lang = s.language;
  if (reblog) {
    const by = isRecord(outer.account) && typeof outer.account.acct === 'string' ? outer.account.acct : null;
    item.extra = { boostedBy: by };
  }
  if (!item.html && media.length > 0) item.html = `<p>[${media[0]?.type === 'image' ? 'Image' : 'Media'}]</p>`;
  return item;
}

/** Mastodon ids are decimal strings that grow over time; compare without Number precision loss. */
export function compareIds(a: string, b: string): number {
  if (a.length !== b.length) return a.length - b.length;
  return a < b ? -1 : a > b ? 1 : 0;
}

/** The numeric id of @acct on its instance (accounts/lookup). */
async function lookupAccountId(ctx: SourceFetchContext<'mastodon'>, instance: string, acct: string): Promise<string> {
  const response = await ctx.runtime.fetcher.fetch({ url: `https://${instance}/api/v1/accounts/lookup?acct=${encodeURIComponent(acct)}`, method: 'GET', headers: { Accept: 'application/json' }, route: 'auto' });
  if (response.status === 401) throw new DigesterError('secret_missing', `${instance} requires a token to look up accounts.`, { status: 401 });
  assertOk(response, { credential: false, what: `@${acct}@${instance}`, now: ctx.runtime.clock.now() });
  const account = parseJson(response.text, 'The account lookup');
  if (!isRecord(account) || typeof account.id !== 'string') throw new DigesterError('parse_failed', 'The account lookup returned no account id.');
  return account.id;
}

export const mastodonClient: SourceClient<'mastodon'> = {
  kind: 'mastodon',
  defaultPullMinutes: 15,

  async resolve(input, runtime) {
    const parsed = parseMastodonAddress(input.address, input.config);

    if (!parsed) throw invalidInput('Enter @user@instance, a profile or hashtag URL, or #tag with an instance.');
    const locator = mastodonLocator(parsed);
    const base: MastodonConfig = {
      kind: 'mastodon',
      instance: parsed.instance,
      mode: parsed.mode,
      includeReplies: input.config?.includeReplies ?? false,
      includeBoosts: input.config?.includeBoosts ?? true,
    };
    const id = sourceId('mastodon', locator);
    if (parsed.mode === 'hashtag') {
      return { kind: 'mastodon', locator, name: `#${parsed.hashtag ?? ''} on ${parsed.instance}`, config: { ...base, hashtag: parsed.hashtag ?? '' }, requiredSecrets: [] };
    }
    if (parsed.mode === 'home') {
      return { kind: 'mastodon', locator, name: `Home · @${parsed.acct ?? ''}@${parsed.instance}`, config: { ...base, acct: parsed.acct ?? '' }, requiredSecrets: [tokenKey(id)] };
    }
    const acct = parsed.acct ?? '';
    const url = `https://${parsed.instance}/api/v1/accounts/lookup?acct=${encodeURIComponent(acct)}`;
    const response = await runtime.fetcher.fetch({ url, method: 'GET', headers: { Accept: 'application/json' }, route: 'auto' });
    if (response.status === 401) throw new DigesterError('secret_missing', `${parsed.instance} requires a token to look up accounts.`, { status: 401 });
    assertOk(response, { credential: false, what: `@${acct}@${parsed.instance}`, now: runtime.clock.now() });
    const account = parseJson(response.text, 'The account lookup');
    if (!isRecord(account) || typeof account.id !== 'string') throw new DigesterError('parse_failed', 'The account lookup returned no account id.');
    const display = typeof account.display_name === 'string' && account.display_name.trim() ? account.display_name.trim() : `@${acct}`;
    return { kind: 'mastodon', locator, name: display, config: { ...base, acct, accountId: account.id }, requiredSecrets: [] };
  },

  async fetch(ctx: SourceFetchContext<'mastodon'>): Promise<SourceFetchResult> {
    const c = ctx.source.config;
    const token = await ctx.runtime.secrets.get(tokenKey(ctx.source.id));
    const sinceId = typeof ctx.cursor?.sinceId === 'string' ? ctx.cursor.sinceId : null;
    const since = sinceId ? `&since_id=${encodeURIComponent(sinceId)}` : '';
    let url: string;
    let accountId: string | null = null;
    if (c.mode === 'home') {
      if (!token) throw new DigesterError('secret_missing', 'The home timeline needs an access token on this device.');
      url = `https://${c.instance}/api/v1/timelines/home?limit=${PAGE_LIMIT}${since}`;
    } else if (c.mode === 'hashtag') {
      url = `https://${c.instance}/api/v1/timelines/tag/${encodeURIComponent(c.hashtag ?? '')}?limit=${PAGE_LIMIT}${since}`;
    } else {
      // A source created without the numeric account id (over MCP) looks it up once and keeps it in
      // its cursor, since a client cannot change the source's config.
      accountId = c.accountId ?? (typeof ctx.cursor?.accountId === 'string' ? ctx.cursor.accountId : null);
      if (!accountId) {
        if (!c.acct) throw new DigesterError('parse_failed', 'This Mastodon source names no account; add it again.');
        accountId = await lookupAccountId(ctx, c.instance, c.acct);
      }
      url = `https://${c.instance}/api/v1/accounts/${encodeURIComponent(accountId)}/statuses?limit=${PAGE_LIMIT}&exclude_replies=${!c.includeReplies}&exclude_reblogs=${!c.includeBoosts}${since}`;
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await ctx.runtime.fetcher.fetch({ url, method: 'GET', headers, route: token ? 'direct-only' : 'auto' });
    if (response.status === 401 && !token) throw new DigesterError('secret_missing', `${c.instance} requires an access token for this timeline.`, { status: 401 });
    assertOk(response, { credential: token !== null, what: ctx.source.name || c.instance, now: ctx.runtime.clock.now() });
    const list = parseJson(response.text, 'The Mastodon timeline');
    if (!Array.isArray(list)) throw new DigesterError('parse_failed', 'The Mastodon timeline is not a list.');
    const statuses = list.filter(isRecord);
    let newest = sinceId;
    const items: RawItem[] = [];
    for (const s of statuses) {
      if (typeof s.id === 'string' && (newest === null || compareIds(s.id, newest) > 0)) newest = s.id;
      if (!c.includeReplies && typeof s.in_reply_to_id === 'string') continue;
      if (!c.includeBoosts && isRecord(s.reblog)) continue;
      const item = statusItem(s);
      if (item) items.push(item);
    }
    // Statuses come newest first; keep the newest maxItems.
    const cursor = { ...(ctx.cursor ?? {}), ...(newest ? { sinceId: newest } : {}), ...(accountId && !c.accountId ? { accountId } : {}) };
    return { items: items.slice(0, ctx.maxItems), cursor };
  },
};
